import axios from 'axios';
import ReconnectingWebSocket from 'reconnecting-websocket';

import { IOrderbook, ITickerSocket, TradeSocket } from 'types/cex/spot';
import { cexExchangesTimeframes, IChartTimeframe, KLine } from 'types/charts';
import { normalizeNumber } from 'utils/formats/normalizeNumber';
import { ECexName } from 'web3';

import { BaseStrategy, IInitArgs } from '../base_strategy';
import { bootstrapStrategy } from '../bootstrap';

const makeDepthChannel = (symbolId: number, depth: number, precision: number) => {
  return `${symbolId}@snapshot_depth@${depth}@${normalizeNumber(1 / 10 ** precision)}`;
};

export class CoinstoreStrategy implements BaseStrategy {
  ws: ReconnectingWebSocket | undefined;
  pingInterval: ReturnType<typeof setInterval> | undefined;

  pair: string;
  onOrderbookUpdate: (v: IOrderbook) => void;
  onTickerUpdate: (v: ITickerSocket) => void;
  klineUpdate: (v: KLine) => void;
  precisionUpdate: (v: number) => void;
  onTradeUpdate: (v: TradeSocket) => void;

  symbolId: number;
  depth: number;
  precision: number;
  depthConfig: { id: number; channel: string; precision: number } | undefined;

  constructor({
    base,
    quote,
    orderbookDepth,
    onOrderbookUpdate,
    onTickerUpdate,
    klineUpdate,
    precisionUpdate,
    onTradeUpdate,
  }: IInitArgs) {
    this.pair = `${base.toUpperCase()}${quote.toUpperCase()}`;
    this.depth = orderbookDepth ?? 20;
    this.precision = 1;
    this.symbolId = 0;
    this.onOrderbookUpdate = onOrderbookUpdate;
    this.onTickerUpdate = onTickerUpdate;
    this.klineUpdate = klineUpdate;
    this.precisionUpdate = precisionUpdate;
    this.onTradeUpdate = onTradeUpdate;
  }

  fetchTickerPrecision = async () => {
    try {
      const res = await axios.post<{
        data: {
          symbolId: number;
          tickSz: number;
        }[];
      }>('https://api.coinstore.com/api/v2/public/config/spot/symbols', {
        symbolCodes: [this.pair],
      });

      let prec = 1;

      if (res.data) {
        prec = res.data.data[0].tickSz;

        this.symbolId = res.data.data[0].symbolId;
      }
      this.precision = prec;
      this.precisionUpdate(prec);
    } catch (error) {
      console.log('Coinstore ticker error: ', error);
    }
  };

  fetchKlines = async ({
    base,
    quote,
    limit,
    from: _from,
    to: _to,
    interval,
  }: {
    base: string;
    quote: string;
    from: number;
    to: number;
    limit: number;
    interval: IChartTimeframe;
  }) => {
    const innerInterval = cexExchangesTimeframes[interval][ECexName.coinstore];

    try {
      const result = await axios.get<{
        data: {
          item: {
            startTime: number;
            close: string;
            open: string;
            high: string;
            low: string;
            volume: string;
          }[];
        };
      }>(
        `https://api.coinstore.com/api/v1/market/kline/${base.toUpperCase()}${quote.toUpperCase()}`,
        {
          params: {
            period: innerInterval,
            size: limit,
          },
        },
      );

      return (result.data.data.item ?? []).map(el => [
        el.startTime * 1000,
        el.open,
        el.high,
        el.low,
        el.close,
        el.volume,
      ]) as KLine[];
    } catch (error) {
      console.log('Coinstore klines error: ', error);
      return [];
    }
  };

  fetchTrades = async ({ base, quote, limit }: { base: string; quote: string; limit: number }) => {
    try {
      const result = await axios.get<{
        data: {
          ts: string;
          price: string;
          volume: string;
          takerSide: string;
        }[];
      }>(
        `https://api.coinstore.com/api/v1/market/trade/${base.toUpperCase()}${quote.toUpperCase()}`,
        { params: { limit } },
      );

      return (
        (result.data.data ?? [])
          .map(item => ({
            price: item.price,
            quantity: item.volume,
            timestamp: item.ts,
            side: item.takerSide.toLowerCase(),
          }))
          .slice(0, 20) ?? []
      );
    } catch (error) {
      console.log('Coinstore trades error: ', error);
      return [];
    }
  };

  connectWebSocket = ({ interval }: { interval: number }) => {
    if (this.ws) {
      this.closeWebSocket();
    }

    const ws = new ReconnectingWebSocket('wss://ws.coinstore.com/s/ws', [], {
      maxRetries: 10,
      minReconnectionDelay: 1000,
    });

    this.ws = ws;

    const intervalChannel = {
      60000: 'min_1',
      300000: 'min_5',
      900000: 'min_15',
      3600000: 'hour_1',
      14400000: 'hour_4',
      86400000: 'day_1',
      604800000: 'week_1',
    }[interval];

    const tickerChannel = `${this.pair}@ticker`;
    const depthChannel = makeDepthChannel(this.symbolId, this.depth, this.precision);
    const klinesChannel = `${this.pair}@kline@${intervalChannel}`;
    const tradesChannel = `${this.pair}@trade`;

    this.pingInterval = setInterval(() => {
      if (this.ws) {
        this.ws.send(JSON.stringify({ op: 'pong', epochMillis: Math.floor(Date.now() / 1000) }));
      }
    }, 60 * 1000);

    this.ws.onopen = () => {
      this.depthConfig = {
        channel: depthChannel,
        id: 11,
        precision: this.precision,
      };

      ws.send(JSON.stringify({ op: 'SUB', channel: [tickerChannel], id: 10 }));
      ws.send(
        JSON.stringify({ op: 'SUB', channel: [this.depthConfig.channel], id: this.depthConfig.id }),
      );
      ws.send(JSON.stringify({ op: 'SUB', channel: [klinesChannel], id: 12 }));
      ws.send(JSON.stringify({ op: 'SUB', channel: [tradesChannel], id: Date.now() }));
    };

    this.ws.onmessage = event => {
      try {
        const data = JSON.parse(event.data);

        if (data.channel === depthChannel) {
          const asks = data.a as [string, string][];
          const bids = data.b as [string, string][];

          this.onOrderbookUpdate({
            bids: bids.map(el => [Number(el[0]).toFixed(this.depthConfig!.precision), el[1]]),
            asks: asks.map(el => [Number(el[0]).toFixed(this.depthConfig!.precision), el[1]]),
          });
        }

        if (data.channel === klinesChannel) {
          const { startTime, open, high, low, close, volume } = data;

          this.klineUpdate([
            intervalChannel === 'day_1' ? startTime + 86400 : startTime,
            open,
            high,
            low,
            close,
            volume,
          ]);
        }

        if (data.T === 'ticker') {
          const { open, high, low, close, volume, amount } = data;

          const change = normalizeNumber(((Number(close) - Number(open)) / Number(open)) * 100);

          this.onTickerUpdate({
            baseVolume: volume,
            change: change,
            close: close,
            high: high,
            last: Number(close).toFixed(this.precision),
            low: low,
            quoteVolume: amount,
          });
        }

        if (data.channel === tradesChannel) {
          const { ts, price, volume, takerSide } = data;

          this.onTradeUpdate({
            price,
            quantity: volume,
            timestamp: ts,
            side: takerSide.toLowerCase(),
          });
        }
      } catch (error) {
        console.log('Coinstore socket error', error);
      }
    };
  };

  closeWebSocket = () => {
    if (this.ws) {
      this.ws.close();
    }
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
    }
  };

  switchOrderbookTick = ({ tick }: { tick: number }) => {
    if (this.ws && this.depthConfig) {
      this.ws.send(
        JSON.stringify({
          op: 'UNSUB',
          channel: [this.depthConfig.channel],
          id: this.depthConfig.id,
        }),
      );

      this.depthConfig = {
        channel: makeDepthChannel(this.symbolId, this.depth, tick),
        id: this.depthConfig.id + 10,
        precision: tick,
      };
      this.ws.send(
        JSON.stringify({
          op: 'SUB',
          channel: [this.depthConfig.channel],
          id: this.depthConfig.id,
        }),
      );
    }
  };

  setOrderbookDepth(depth: number) {
    if (this.ws && this.depthConfig) {
      this.ws.send(
        JSON.stringify({
          op: 'UNSUB',
          channel: [this.depthConfig.channel],
          id: this.depthConfig.id,
        }),
      );
    }

    this.depth = depth;

    if (this.ws) {
      const nextDepthChannel = makeDepthChannel(this.symbolId, this.depth, this.precision);
      this.depthConfig = {
        channel: nextDepthChannel,
        id: 11,
        precision: this.precision,
      };
      this.ws.send(
        JSON.stringify({ op: 'SUB', channel: [this.depthConfig.channel], id: this.depthConfig.id }),
      );
    }
  }

  getOrderbookTicks = (prec: number) => [
    {
      value: normalizeNumber(1 / 10 ** prec),
      tick: prec,
      id: prec,
    },
  ];

  fetch = async () => {
    await this.fetchTickerPrecision();
  };

  static init = async (args: IInitArgs) => {
    const instance = new CoinstoreStrategy(args);

    await bootstrapStrategy(instance);

    return instance;
  };
}
